/**
 * WeaveIn.ai Platform - Pharmacy Benefits Manager (PBM) Component
 * Manages pharmacy benefits, drug formularies, and prescription coverage
 */

import React, { useState, useEffect } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Pill,
  AlertTriangle,
  CheckCircle,
  X,
  Loader2,
} from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

interface DrugTier {
  id: number;
  tier_number: number;
  tier_name: string;
  description: string;
  copay_retail_30day: number;
  copay_retail_90day: number;
  copay_mail_order_90day: number;
  coinsurance_percentage: number;
  deductible_applies: boolean;
  quantity_limits: boolean;
  prior_auth_required: boolean;
  step_therapy_required: boolean;
}

interface FormularyDrug {
  id: number;
  ndc_code: string;
  drug_name: string;
  generic_name: string;
  brand_name?: string;
  drug_class: string;
  therapeutic_category: string;
  tier_id: number;
  tier_name: string;
  is_generic: boolean;
  is_specialty: boolean;
  requires_prior_auth: boolean;
  requires_step_therapy: boolean;
  quantity_limit?: string;
  age_limit?: string;
  gender_limit?: string;
  diagnosis_restriction?: string;
  status: 'active' | 'inactive' | 'pending_removal';
}

interface PharmacyNetwork {
  id: number;
  network_name: string;
  network_type: 'retail' | 'mail_order' | 'specialty' | 'preferred';
  pharmacy_count: number;
  geographic_coverage: string;
  is_preferred: boolean;
  discount_percentage: number;
  dispensing_fee: number;
}

interface PriorAuthRule {
  id: number;
  drug_name: string;
  drug_class: string;
  criteria: string[];
  required_documentation: string[];
  approval_duration_days: number;
  auto_approve_criteria?: string;
}

interface StepTherapyProtocol {
  id: number;
  target_drug: string;
  required_first_line: string[];
  required_duration_days: number;
  exception_criteria: string[];
}

interface PharmacyBenefitsManagerProps {
  planId: number;
  employerId?: number;
  isReadOnly?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_TIERS: Omit<DrugTier, 'id'>[] = [
  {
    tier_number: 1,
    tier_name: 'Preferred Generic',
    description: 'Low-cost generic medications',
    copay_retail_30day: 10,
    copay_retail_90day: 25,
    copay_mail_order_90day: 20,
    coinsurance_percentage: 0,
    deductible_applies: false,
    quantity_limits: false,
    prior_auth_required: false,
    step_therapy_required: false,
  },
  {
    tier_number: 2,
    tier_name: 'Non-Preferred Generic',
    description: 'Other generic medications',
    copay_retail_30day: 20,
    copay_retail_90day: 50,
    copay_mail_order_90day: 40,
    coinsurance_percentage: 0,
    deductible_applies: false,
    quantity_limits: false,
    prior_auth_required: false,
    step_therapy_required: false,
  },
  {
    tier_number: 3,
    tier_name: 'Preferred Brand',
    description: 'Preferred brand-name medications',
    copay_retail_30day: 35,
    copay_retail_90day: 87.50,
    copay_mail_order_90day: 70,
    coinsurance_percentage: 20,
    deductible_applies: true,
    quantity_limits: true,
    prior_auth_required: false,
    step_therapy_required: true,
  },
  {
    tier_number: 4,
    tier_name: 'Non-Preferred Brand',
    description: 'Other brand-name medications',
    copay_retail_30day: 60,
    copay_retail_90day: 150,
    copay_mail_order_90day: 120,
    coinsurance_percentage: 30,
    deductible_applies: true,
    quantity_limits: true,
    prior_auth_required: true,
    step_therapy_required: true,
  },
  {
    tier_number: 5,
    tier_name: 'Specialty',
    description: 'High-cost specialty medications',
    copay_retail_30day: 150,
    copay_retail_90day: 450,
    copay_mail_order_90day: 375,
    coinsurance_percentage: 25,
    deductible_applies: true,
    quantity_limits: true,
    prior_auth_required: true,
    step_therapy_required: true,
  },
];

const THERAPEUTIC_CATEGORIES = [
  'Cardiovascular',
  'Diabetes',
  'Mental Health',
  'Pain Management',
  'Respiratory',
  'Gastrointestinal',
  'Infectious Disease',
  'Oncology',
  'Immunology',
  'Dermatology',
  'Neurology',
  'Endocrine',
  'Musculoskeletal',
  'Ophthalmology',
  'Other',
];

// ============================================================================
// Component
// ============================================================================

export default function PharmacyBenefitsManager({
  planId,
  employerId,
  isReadOnly = false,
}: PharmacyBenefitsManagerProps) {
  const { t } = useAppTranslation();
  const [activeTab, setActiveTab] = useState('tiers');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data state
  const [drugTiers, setDrugTiers] = useState<DrugTier[]>([]);
  const [formularyDrugs, setFormularyDrugs] = useState<FormularyDrug[]>([]);
  const [pharmacyNetworks, setPharmacyNetworks] = useState<PharmacyNetwork[]>([]);
  const [priorAuthRules, setPriorAuthRules] = useState<PriorAuthRule[]>([]);
  const [stepTherapyProtocols, setStepTherapyProtocols] = useState<StepTherapyProtocol[]>([]);

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showTierDialog, setShowTierDialog] = useState(false);
  const [showDrugDialog, setShowDrugDialog] = useState(false);
  const [showNetworkDialog, setShowNetworkDialog] = useState(false);
  const [showPriorAuthDialog, setShowPriorAuthDialog] = useState(false);
  const [showStepTherapyDialog, setShowStepTherapyDialog] = useState(false);
  const [editingTier, setEditingTier] = useState<DrugTier | null>(null);
  const [editingDrug, setEditingDrug] = useState<FormularyDrug | null>(null);
  const [editingNetwork, setEditingNetwork] = useState<PharmacyNetwork | null>(null);
  const [editingPriorAuth, setEditingPriorAuth] = useState<PriorAuthRule | null>(null);
  const [editingStepTherapy, setEditingStepTherapy] = useState<StepTherapyProtocol | null>(null);

  // Form state for tier
  const [tierForm, setTierForm] = useState<Partial<DrugTier>>({
    tier_number: 1,
    tier_name: '',
    description: '',
    copay_retail_30day: 0,
    copay_retail_90day: 0,
    copay_mail_order_90day: 0,
    coinsurance_percentage: 0,
    deductible_applies: false,
    quantity_limits: false,
    prior_auth_required: false,
    step_therapy_required: false,
  });

  // Form state for drug
  const [drugForm, setDrugForm] = useState<Partial<FormularyDrug>>({
    ndc_code: '',
    drug_name: '',
    generic_name: '',
    brand_name: '',
    drug_class: '',
    therapeutic_category: '',
    tier_id: 1,
    is_generic: true,
    is_specialty: false,
    requires_prior_auth: false,
    requires_step_therapy: false,
    status: 'active',
  });

  // Form state for network
  const [networkForm, setNetworkForm] = useState<Partial<PharmacyNetwork>>({
    network_name: '',
    network_type: 'retail',
    pharmacy_count: 0,
    geographic_coverage: '',
    is_preferred: false,
    discount_percentage: 0,
    dispensing_fee: 0,
  });

  // Form state for prior auth rule
  const [priorAuthForm, setPriorAuthForm] = useState<Partial<PriorAuthRule>>({
    drug_name: '',
    drug_class: '',
    criteria: [],
    required_documentation: [],
    approval_duration_days: 365,
    auto_approve_criteria: '',
  });
  const [criteriaInput, setCriteriaInput] = useState('');
  const [documentationInput, setDocumentationInput] = useState('');

  // Form state for step therapy protocol
  const [stepTherapyForm, setStepTherapyForm] = useState<Partial<StepTherapyProtocol>>({
    target_drug: '',
    required_first_line: [],
    required_duration_days: 30,
    exception_criteria: [],
  });
  const [firstLineInput, setFirstLineInput] = useState('');
  const [exceptionInput, setExceptionInput] = useState('');

  // Load data
  useEffect(() => {
    loadData();
  }, [planId, employerId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Simulate API call - in production, fetch from backend
      await new Promise(resolve => setTimeout(resolve, 500));

      // Initialize with default tiers if empty
      setDrugTiers(DEFAULT_TIERS.map((tier, index) => ({ ...tier, id: index + 1 })));

      // Sample formulary drugs
      setFormularyDrugs([
        {
          id: 1,
          ndc_code: '00002-4462-30',
          drug_name: 'Metformin HCl',
          generic_name: 'Metformin Hydrochloride',
          brand_name: 'Glucophage',
          drug_class: 'Biguanides',
          therapeutic_category: 'Diabetes',
          tier_id: 1,
          tier_name: 'Preferred Generic',
          is_generic: true,
          is_specialty: false,
          requires_prior_auth: false,
          requires_step_therapy: false,
          status: 'active',
        },
        {
          id: 2,
          ndc_code: '00074-3799-13',
          drug_name: 'Humira',
          generic_name: 'Adalimumab',
          brand_name: 'Humira',
          drug_class: 'TNF Inhibitors',
          therapeutic_category: 'Immunology',
          tier_id: 5,
          tier_name: 'Specialty',
          is_generic: false,
          is_specialty: true,
          requires_prior_auth: true,
          requires_step_therapy: true,
          quantity_limit: '2 pens per 28 days',
          status: 'active',
        },
        {
          id: 3,
          ndc_code: '00006-0749-31',
          drug_name: 'Lisinopril',
          generic_name: 'Lisinopril',
          drug_class: 'ACE Inhibitors',
          therapeutic_category: 'Cardiovascular',
          tier_id: 1,
          tier_name: 'Preferred Generic',
          is_generic: true,
          is_specialty: false,
          requires_prior_auth: false,
          requires_step_therapy: false,
          status: 'active',
        },
      ]);

      // Sample pharmacy networks
      setPharmacyNetworks([
        {
          id: 1,
          network_name: 'National Retail Network',
          network_type: 'retail',
          pharmacy_count: 68000,
          geographic_coverage: 'Nationwide',
          is_preferred: true,
          discount_percentage: 15,
          dispensing_fee: 1.50,
        },
        {
          id: 2,
          network_name: 'Express Scripts Mail Order',
          network_type: 'mail_order',
          pharmacy_count: 3,
          geographic_coverage: 'Nationwide',
          is_preferred: true,
          discount_percentage: 25,
          dispensing_fee: 0,
        },
        {
          id: 3,
          network_name: 'Specialty Pharmacy Network',
          network_type: 'specialty',
          pharmacy_count: 500,
          geographic_coverage: 'Nationwide',
          is_preferred: false,
          discount_percentage: 10,
          dispensing_fee: 5.00,
        },
      ]);

      // Sample prior auth rules
      setPriorAuthRules([
        {
          id: 1,
          drug_name: 'Humira',
          drug_class: 'TNF Inhibitors',
          criteria: [
            'Diagnosis of rheumatoid arthritis, psoriatic arthritis, or Crohn\'s disease',
            'Trial and failure of at least 2 DMARDs',
            'No active infections',
          ],
          required_documentation: [
            'Medical records documenting diagnosis',
            'Documentation of prior DMARD trials',
            'Recent TB test results',
          ],
          approval_duration_days: 365,
        },
      ]);

      // Sample step therapy protocols
      setStepTherapyProtocols([
        {
          id: 1,
          target_drug: 'Brand NSAID',
          required_first_line: ['Ibuprofen', 'Naproxen', 'Meloxicam'],
          required_duration_days: 30,
          exception_criteria: ['Documented allergy to first-line agents', 'GI bleeding history'],
        },
      ]);

      setLoading(false);
    } catch (err) {
      console.error('Error loading pharmacy data:', err);
      setError('Failed to load pharmacy benefits data');
      setLoading(false);
    }
  };

  // Filter drugs based on search and category
  const filteredDrugs = formularyDrugs.filter(drug => {
    const matchesSearch = searchTerm === '' ||
      drug.drug_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      drug.generic_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      drug.ndc_code.includes(searchTerm);
    const matchesCategory = selectedCategory === 'all' || drug.therapeutic_category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Handlers
  const handleSaveTier = async () => {
    if (editingTier) {
      setDrugTiers(prev => prev.map(tier =>
        tier.id === editingTier.id ? { ...tier, ...tierForm } : tier
      ));
    } else {
      const newTier: DrugTier = {
        ...tierForm as DrugTier,
        id: Math.max(...drugTiers.map(tier => tier.id), 0) + 1,
      };
      setDrugTiers(prev => [...prev, newTier]);
    }
    setShowTierDialog(false);
    setEditingTier(null);
    resetTierForm();
  };

  const handleSaveDrug = async () => {
    const tier = drugTiers.find(dt => dt.id === drugForm.tier_id);
    if (editingDrug) {
      setFormularyDrugs(prev => prev.map(drug =>
        drug.id === editingDrug.id ? { ...drug, ...drugForm, tier_name: tier?.tier_name || '' } as FormularyDrug : drug
      ));
    } else {
      const newDrug: FormularyDrug = {
        ...drugForm as FormularyDrug,
        id: Math.max(...formularyDrugs.map(d => d.id), 0) + 1,
        tier_name: tier?.tier_name || '',
      };
      setFormularyDrugs(prev => [...prev, newDrug]);
    }
    setShowDrugDialog(false);
    setEditingDrug(null);
    resetDrugForm();
  };

  const handleDeleteTier = (tierId: number) => {
    if (window.confirm('Are you sure you want to delete this tier? Drugs in this tier will need to be reassigned.')) {
      setDrugTiers(prev => prev.filter(tier => tier.id !== tierId));
    }
  };

  const handleDeleteDrug = (drugId: number) => {
    if (window.confirm('Are you sure you want to remove this drug from the formulary?')) {
      setFormularyDrugs(prev => prev.filter(drug => drug.id !== drugId));
    }
  };

  const resetTierForm = () => {
    setTierForm({
      tier_number: drugTiers.length + 1,
      tier_name: '',
      description: '',
      copay_retail_30day: 0,
      copay_retail_90day: 0,
      copay_mail_order_90day: 0,
      coinsurance_percentage: 0,
      deductible_applies: false,
      quantity_limits: false,
      prior_auth_required: false,
      step_therapy_required: false,
    });
  };

  const resetDrugForm = () => {
    setDrugForm({
      ndc_code: '',
      drug_name: '',
      generic_name: '',
      brand_name: '',
      drug_class: '',
      therapeutic_category: '',
      tier_id: 1,
      is_generic: true,
      is_specialty: false,
      requires_prior_auth: false,
      requires_step_therapy: false,
      status: 'active',
    });
  };

  const openEditTier = (tier: DrugTier) => {
    setEditingTier(tier);
    setTierForm(tier);
    setShowTierDialog(true);
  };

  const openEditDrug = (drug: FormularyDrug) => {
    setEditingDrug(drug);
    setDrugForm(drug);
    setShowDrugDialog(true);
  };

  // Network handlers
  const handleSaveNetwork = async () => {
    if (editingNetwork) {
      setPharmacyNetworks(prev => prev.map(network =>
        network.id === editingNetwork.id ? { ...network, ...networkForm } as PharmacyNetwork : network
      ));
    } else {
      const newNetwork: PharmacyNetwork = {
        ...networkForm as PharmacyNetwork,
        id: Math.max(...pharmacyNetworks.map(n => n.id), 0) + 1,
      };
      setPharmacyNetworks(prev => [...prev, newNetwork]);
    }
    setShowNetworkDialog(false);
    setEditingNetwork(null);
    resetNetworkForm();
  };

  const resetNetworkForm = () => {
    setNetworkForm({
      network_name: '',
      network_type: 'retail',
      pharmacy_count: 0,
      geographic_coverage: '',
      is_preferred: false,
      discount_percentage: 0,
      dispensing_fee: 0,
    });
  };

  const openEditNetwork = (network: PharmacyNetwork) => {
    setEditingNetwork(network);
    setNetworkForm(network);
    setShowNetworkDialog(true);
  };

  const handleDeleteNetwork = (networkId: number) => {
    if (window.confirm('Are you sure you want to delete this pharmacy network?')) {
      setPharmacyNetworks(prev => prev.filter(n => n.id !== networkId));
    }
  };

  // Prior Auth handlers
  const handleSavePriorAuth = async () => {
    if (editingPriorAuth) {
      setPriorAuthRules(prev => prev.map(rule =>
        rule.id === editingPriorAuth.id ? { ...rule, ...priorAuthForm } as PriorAuthRule : rule
      ));
    } else {
      const newRule: PriorAuthRule = {
        ...priorAuthForm as PriorAuthRule,
        id: Math.max(...priorAuthRules.map(r => r.id), 0) + 1,
      };
      setPriorAuthRules(prev => [...prev, newRule]);
    }
    setShowPriorAuthDialog(false);
    setEditingPriorAuth(null);
    resetPriorAuthForm();
  };

  const resetPriorAuthForm = () => {
    setPriorAuthForm({
      drug_name: '',
      drug_class: '',
      criteria: [],
      required_documentation: [],
      approval_duration_days: 365,
      auto_approve_criteria: '',
    });
    setCriteriaInput('');
    setDocumentationInput('');
  };

  const openEditPriorAuth = (rule: PriorAuthRule) => {
    setEditingPriorAuth(rule);
    setPriorAuthForm(rule);
    setShowPriorAuthDialog(true);
  };

  const handleDeletePriorAuth = (ruleId: number) => {
    if (window.confirm('Are you sure you want to delete this prior authorization rule?')) {
      setPriorAuthRules(prev => prev.filter(r => r.id !== ruleId));
    }
  };

  const addCriteria = () => {
    if (criteriaInput.trim()) {
      setPriorAuthForm(prev => ({
        ...prev,
        criteria: [...(prev.criteria || []), criteriaInput.trim()],
      }));
      setCriteriaInput('');
    }
  };

  const removeCriteria = (index: number) => {
    setPriorAuthForm(prev => ({
      ...prev,
      criteria: prev.criteria?.filter((_, i) => i !== index) || [],
    }));
  };

  const addDocumentation = () => {
    if (documentationInput.trim()) {
      setPriorAuthForm(prev => ({
        ...prev,
        required_documentation: [...(prev.required_documentation || []), documentationInput.trim()],
      }));
      setDocumentationInput('');
    }
  };

  const removeDocumentation = (index: number) => {
    setPriorAuthForm(prev => ({
      ...prev,
      required_documentation: prev.required_documentation?.filter((_, i) => i !== index) || [],
    }));
  };

  // Step Therapy handlers
  const handleSaveStepTherapy = async () => {
    if (editingStepTherapy) {
      setStepTherapyProtocols(prev => prev.map(protocol =>
        protocol.id === editingStepTherapy.id ? { ...protocol, ...stepTherapyForm } as StepTherapyProtocol : protocol
      ));
    } else {
      const newProtocol: StepTherapyProtocol = {
        ...stepTherapyForm as StepTherapyProtocol,
        id: Math.max(...stepTherapyProtocols.map(p => p.id), 0) + 1,
      };
      setStepTherapyProtocols(prev => [...prev, newProtocol]);
    }
    setShowStepTherapyDialog(false);
    setEditingStepTherapy(null);
    resetStepTherapyForm();
  };

  const resetStepTherapyForm = () => {
    setStepTherapyForm({
      target_drug: '',
      required_first_line: [],
      required_duration_days: 30,
      exception_criteria: [],
    });
    setFirstLineInput('');
    setExceptionInput('');
  };

  const openEditStepTherapy = (protocol: StepTherapyProtocol) => {
    setEditingStepTherapy(protocol);
    setStepTherapyForm(protocol);
    setShowStepTherapyDialog(true);
  };

  const handleDeleteStepTherapy = (protocolId: number) => {
    if (window.confirm('Are you sure you want to delete this step therapy protocol?')) {
      setStepTherapyProtocols(prev => prev.filter(p => p.id !== protocolId));
    }
  };

  const addFirstLine = () => {
    if (firstLineInput.trim()) {
      setStepTherapyForm(prev => ({
        ...prev,
        required_first_line: [...(prev.required_first_line || []), firstLineInput.trim()],
      }));
      setFirstLineInput('');
    }
  };

  const removeFirstLine = (index: number) => {
    setStepTherapyForm(prev => ({
      ...prev,
      required_first_line: prev.required_first_line?.filter((_, i) => i !== index) || [],
    }));
  };

  const addException = () => {
    if (exceptionInput.trim()) {
      setStepTherapyForm(prev => ({
        ...prev,
        exception_criteria: [...(prev.exception_criteria || []), exceptionInput.trim()],
      }));
      setExceptionInput('');
    }
  };

  const removeException = (index: number) => {
    setStepTherapyForm(prev => ({
      ...prev,
      exception_criteria: prev.exception_criteria?.filter((_, i) => i !== index) || [],
    }));
  };

  // Get tier color variant based on tier number
  const getTierVariant = (tierNumber: number): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (tierNumber) {
      case 1: return 'default';
      case 2: return 'secondary';
      case 3: return 'outline';
      case 4: return 'destructive';
      case 5: return 'destructive';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Pill className="h-6 w-6 text-primary" />
            {t('pharmacy.title', 'Pharmacy Benefits Manager')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('pharmacy.description', 'Manage drug formulary, tiers, and pharmacy networks')}
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="flex justify-between items-center">
            <span>{error}</span>
            <button onClick={() => setError(null)}>
              <X className="h-4 w-4" />
            </button>
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300">
          <TabsList className="w-max min-w-full justify-start flex-nowrap">
            <TabsTrigger value="tiers" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <span className="hidden sm:inline">{t('pharmacy.drugTiers', 'Drug Tiers')}</span>
              <span className="sm:hidden">Tiers</span>
            </TabsTrigger>
            <TabsTrigger value="formulary" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              {t('pharmacy.formulary', 'Formulary')}
            </TabsTrigger>
            <TabsTrigger value="networks" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <span className="hidden sm:inline">{t('pharmacy.networks', 'Pharmacy Networks')}</span>
              <span className="sm:hidden">Networks</span>
            </TabsTrigger>
            <TabsTrigger value="priorAuth" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <span className="hidden sm:inline">{t('pharmacy.priorAuth', 'Prior Authorization')}</span>
              <span className="sm:hidden">Prior Auth</span>
            </TabsTrigger>
            <TabsTrigger value="stepTherapy" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <span className="hidden sm:inline">{t('pharmacy.stepTherapy', 'Step Therapy')}</span>
              <span className="sm:hidden">Step Therapy</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Drug Tiers Tab */}
        <TabsContent value="tiers">
          <div className="mb-4 flex justify-between items-center">
            <h3 className="text-lg font-semibold">{t('pharmacy.drugTierStructure', 'Drug Tier Structure')}</h3>
            {!isReadOnly && (
              <Button
                onClick={() => {
                  resetTierForm();
                  setShowTierDialog(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('pharmacy.addTier', 'Add Tier')}
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {drugTiers.sort((a, b) => a.tier_number - b.tier_number).map(tier => (
              <Card key={tier.id}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <Badge variant={getTierVariant(tier.tier_number)} className="mb-2">
                        Tier {tier.tier_number}
                      </Badge>
                      <h4 className="text-lg font-semibold">{tier.tier_name}</h4>
                      <p className="text-sm text-muted-foreground">{tier.description}</p>
                    </div>
                    {!isReadOnly && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEditTier(tier)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteTier(tier.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    )}
                  </div>

                  <hr className="my-4 border-border" />

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Retail 30-day</p>
                      <p className="text-sm font-medium">${tier.copay_retail_30day}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Retail 90-day</p>
                      <p className="text-sm font-medium">${tier.copay_retail_90day}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Mail Order 90-day</p>
                      <p className="text-sm font-medium">${tier.copay_mail_order_90day}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Coinsurance</p>
                      <p className="text-sm font-medium">{tier.coinsurance_percentage}%</p>
                    </div>
                  </div>

                  <div className="mt-4 flex gap-2 flex-wrap">
                    {tier.deductible_applies && <Badge variant="outline">Deductible</Badge>}
                    {tier.quantity_limits && <Badge variant="outline">Qty Limits</Badge>}
                    {tier.prior_auth_required && <Badge variant="outline">Prior Auth</Badge>}
                    {tier.step_therapy_required && <Badge variant="outline">Step Therapy</Badge>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Formulary Tab */}
        <TabsContent value="formulary">
          <div className="mb-4 flex flex-wrap gap-4 justify-between">
            <div className="flex gap-4 flex-wrap">
              <div className="relative min-w-[250px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('pharmacy.searchDrugs', 'Search drugs...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="min-w-[180px]">
                  <SelectValue placeholder={t('pharmacy.category', 'Category')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {THERAPEUTIC_CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!isReadOnly && (
              <Button
                onClick={() => {
                  resetDrugForm();
                  setShowDrugDialog(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('pharmacy.addDrug', 'Add Drug')}
              </Button>
            )}
          </div>

          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Drug Name</TableHead>
                  <TableHead>Generic Name</TableHead>
                  <TableHead>NDC Code</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Restrictions</TableHead>
                  <TableHead>Status</TableHead>
                  {!isReadOnly && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDrugs.map(drug => (
                  <TableRow key={drug.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{drug.drug_name}</div>
                        {drug.brand_name && drug.brand_name !== drug.drug_name && (
                          <div className="text-xs text-muted-foreground">
                            Brand: {drug.brand_name}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{drug.generic_name}</TableCell>
                    <TableCell>
                      <code className="text-sm">{drug.ndc_code}</code>
                    </TableCell>
                    <TableCell>{drug.therapeutic_category}</TableCell>
                    <TableCell>
                      <Badge variant={getTierVariant(drug.tier_id)}>
                        {drug.tier_name}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {drug.is_specialty && <Badge variant="destructive">Specialty</Badge>}
                        {drug.requires_prior_auth && <Badge variant="outline">PA</Badge>}
                        {drug.requires_step_therapy && <Badge variant="outline">ST</Badge>}
                        {drug.quantity_limit && <Badge variant="outline">QL</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={drug.status === 'active' ? 'default' : drug.status === 'pending_removal' ? 'secondary' : 'outline'}>
                        {drug.status}
                      </Badge>
                    </TableCell>
                    {!isReadOnly && (
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => openEditDrug(drug)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteDrug(drug.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {filteredDrugs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No drugs found matching your criteria
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Pharmacy Networks Tab */}
        <TabsContent value="networks">
          <div className="mb-4 flex justify-between items-center">
            <h3 className="text-lg font-semibold">{t('pharmacy.pharmacyNetworks', 'Pharmacy Networks')}</h3>
            {!isReadOnly && (
              <Button
                onClick={() => {
                  resetNetworkForm();
                  setShowNetworkDialog(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('pharmacy.addNetwork', 'Add Network')}
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pharmacyNetworks.map(network => (
              <Card key={network.id}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-lg font-semibold">{network.network_name}</h4>
                        {network.is_preferred && (
                          <Badge variant="default">Preferred</Badge>
                        )}
                      </div>
                      <Badge variant="outline">
                        {network.network_type.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                    {!isReadOnly && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEditNetwork(network)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteNetwork(network.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Pharmacy Count</p>
                      <p className="text-base font-medium">{network.pharmacy_count.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Coverage</p>
                      <p className="text-base font-medium">{network.geographic_coverage}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Discount</p>
                      <p className="text-base font-medium">{network.discount_percentage}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Dispensing Fee</p>
                      <p className="text-base font-medium">${network.dispensing_fee.toFixed(2)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Prior Authorization Tab */}
        <TabsContent value="priorAuth">
          <div className="mb-4 flex justify-between items-center">
            <h3 className="text-lg font-semibold">{t('pharmacy.priorAuthRules', 'Prior Authorization Rules')}</h3>
            {!isReadOnly && (
              <Button
                onClick={() => {
                  resetPriorAuthForm();
                  setShowPriorAuthDialog(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('pharmacy.addRule', 'Add Rule')}
              </Button>
            )}
          </div>

          <div className="space-y-4">
            {priorAuthRules.map(rule => (
              <Card key={rule.id}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mt-1" />
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h4 className="font-semibold">{rule.drug_name}</h4>
                          <p className="text-sm text-muted-foreground">{rule.drug_class}</p>
                        </div>
                        {!isReadOnly && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openEditPriorAuth(rule)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeletePriorAuth(rule.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h5 className="text-sm font-medium mb-2">Approval Criteria</h5>
                          <ul className="list-disc list-inside space-y-1">
                            {rule.criteria.map((criterion, idx) => (
                              <li key={idx} className="text-sm">{criterion}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h5 className="text-sm font-medium mb-2">Required Documentation</h5>
                          <ul className="list-disc list-inside space-y-1">
                            {rule.required_documentation.map((doc, idx) => (
                              <li key={idx} className="text-sm">{doc}</li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      <div className="mt-4">
                        <Badge variant="outline" className="gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Approval Duration: {rule.approval_duration_days} days
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {priorAuthRules.length === 0 && (
              <Alert>
                <AlertDescription>
                  No prior authorization rules configured. Add rules to require approval for specific medications.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </TabsContent>

        {/* Step Therapy Tab */}
        <TabsContent value="stepTherapy">
          <div className="mb-4 flex justify-between items-center">
            <h3 className="text-lg font-semibold">{t('pharmacy.stepTherapyProtocols', 'Step Therapy Protocols')}</h3>
            {!isReadOnly && (
              <Button
                onClick={() => {
                  resetStepTherapyForm();
                  setShowStepTherapyDialog(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('pharmacy.addProtocol', 'Add Protocol')}
              </Button>
            )}
          </div>

          <div className="space-y-4">
            {stepTherapyProtocols.map(protocol => (
              <Card key={protocol.id}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-lg font-semibold">Target: {protocol.target_drug}</h4>
                      <p className="text-sm text-muted-foreground">
                        Requires trial of first-line agents for {protocol.required_duration_days} days
                      </p>
                    </div>
                    {!isReadOnly && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEditStepTherapy(protocol)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteStepTherapy(protocol.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <div>
                      <h5 className="text-sm font-medium mb-2">Required First-Line Agents</h5>
                      <div className="flex gap-2 flex-wrap">
                        {protocol.required_first_line.map((drug, idx) => (
                          <Badge key={idx} variant="default">{drug}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h5 className="text-sm font-medium mb-2">Exception Criteria</h5>
                      <ul className="list-disc list-inside space-y-1">
                        {protocol.exception_criteria.map((exc, idx) => (
                          <li key={idx} className="text-sm">{exc}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {stepTherapyProtocols.length === 0 && (
              <Alert>
                <AlertDescription>
                  No step therapy protocols configured. Add protocols to require trial of first-line medications before approving alternatives.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Drug Tier Dialog */}
      <Dialog open={showTierDialog} onOpenChange={setShowTierDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTier ? 'Edit Drug Tier' : 'Add Drug Tier'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="md:col-span-1">
              <Label htmlFor="tier_number">Tier Number</Label>
              <Input
                id="tier_number"
                type="number"
                value={tierForm.tier_number}
                onChange={(e) => setTierForm({ ...tierForm, tier_number: parseInt(e.target.value) })}
              />
            </div>
            <div className="md:col-span-1">
              <Label htmlFor="tier_name">Tier Name</Label>
              <Input
                id="tier_name"
                value={tierForm.tier_name}
                onChange={(e) => setTierForm({ ...tierForm, tier_name: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={tierForm.description}
                onChange={(e) => setTierForm({ ...tierForm, description: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <h4 className="text-sm font-medium mb-2">Cost Sharing</h4>
              <hr className="border-border mb-4" />
            </div>
            <div>
              <Label htmlFor="copay_retail_30day">Retail 30-day Copay</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="copay_retail_30day"
                  type="number"
                  value={tierForm.copay_retail_30day}
                  onChange={(e) => setTierForm({ ...tierForm, copay_retail_30day: parseFloat(e.target.value) })}
                  className="pl-7"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="copay_retail_90day">Retail 90-day Copay</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="copay_retail_90day"
                  type="number"
                  value={tierForm.copay_retail_90day}
                  onChange={(e) => setTierForm({ ...tierForm, copay_retail_90day: parseFloat(e.target.value) })}
                  className="pl-7"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="copay_mail_order_90day">Mail Order 90-day Copay</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="copay_mail_order_90day"
                  type="number"
                  value={tierForm.copay_mail_order_90day}
                  onChange={(e) => setTierForm({ ...tierForm, copay_mail_order_90day: parseFloat(e.target.value) })}
                  className="pl-7"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="coinsurance_percentage">Coinsurance</Label>
              <div className="relative">
                <Input
                  id="coinsurance_percentage"
                  type="number"
                  value={tierForm.coinsurance_percentage}
                  onChange={(e) => setTierForm({ ...tierForm, coinsurance_percentage: parseFloat(e.target.value) })}
                  className="pr-7"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">%</span>
              </div>
            </div>
            <div className="md:col-span-2">
              <h4 className="text-sm font-medium mb-2">Requirements</h4>
              <hr className="border-border mb-4" />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="deductible_applies"
                checked={tierForm.deductible_applies}
                onCheckedChange={(checked) => setTierForm({ ...tierForm, deductible_applies: checked })}
              />
              <Label htmlFor="deductible_applies">Deductible Applies</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="quantity_limits"
                checked={tierForm.quantity_limits}
                onCheckedChange={(checked) => setTierForm({ ...tierForm, quantity_limits: checked })}
              />
              <Label htmlFor="quantity_limits">Quantity Limits</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="prior_auth_required"
                checked={tierForm.prior_auth_required}
                onCheckedChange={(checked) => setTierForm({ ...tierForm, prior_auth_required: checked })}
              />
              <Label htmlFor="prior_auth_required">Prior Auth Required</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="step_therapy_required"
                checked={tierForm.step_therapy_required}
                onCheckedChange={(checked) => setTierForm({ ...tierForm, step_therapy_required: checked })}
              />
              <Label htmlFor="step_therapy_required">Step Therapy</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTierDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveTier}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drug Dialog */}
      <Dialog open={showDrugDialog} onOpenChange={setShowDrugDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingDrug ? 'Edit Formulary Drug' : 'Add Drug to Formulary'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div>
              <Label htmlFor="drug_name">Drug Name *</Label>
              <Input
                id="drug_name"
                value={drugForm.drug_name}
                onChange={(e) => setDrugForm({ ...drugForm, drug_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="generic_name">Generic Name *</Label>
              <Input
                id="generic_name"
                value={drugForm.generic_name}
                onChange={(e) => setDrugForm({ ...drugForm, generic_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="brand_name">Brand Name</Label>
              <Input
                id="brand_name"
                value={drugForm.brand_name}
                onChange={(e) => setDrugForm({ ...drugForm, brand_name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="ndc_code">NDC Code</Label>
              <Input
                id="ndc_code"
                value={drugForm.ndc_code}
                onChange={(e) => setDrugForm({ ...drugForm, ndc_code: e.target.value })}
                placeholder="00000-0000-00"
              />
            </div>
            <div>
              <Label htmlFor="drug_class">Drug Class</Label>
              <Input
                id="drug_class"
                value={drugForm.drug_class}
                onChange={(e) => setDrugForm({ ...drugForm, drug_class: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="therapeutic_category">Therapeutic Category</Label>
              <Select
                value={drugForm.therapeutic_category}
                onValueChange={(value) => setDrugForm({ ...drugForm, therapeutic_category: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {THERAPEUTIC_CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="tier_id">Drug Tier</Label>
              <Select
                value={drugForm.tier_id?.toString()}
                onValueChange={(value) => setDrugForm({ ...drugForm, tier_id: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select tier" />
                </SelectTrigger>
                <SelectContent>
                  {drugTiers.map(tier => (
                    <SelectItem key={tier.id} value={tier.id.toString()}>
                      Tier {tier.tier_number}: {tier.tier_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <Select
                value={drugForm.status}
                onValueChange={(value) => setDrugForm({ ...drugForm, status: value as 'active' | 'inactive' | 'pending_removal' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="pending_removal">Pending Removal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="quantity_limit">Quantity Limit</Label>
              <Input
                id="quantity_limit"
                value={drugForm.quantity_limit || ''}
                onChange={(e) => setDrugForm({ ...drugForm, quantity_limit: e.target.value })}
                placeholder="e.g., 30 tablets per 30 days"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="is_generic"
                checked={drugForm.is_generic}
                onCheckedChange={(checked) => setDrugForm({ ...drugForm, is_generic: checked })}
              />
              <Label htmlFor="is_generic">Generic</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="is_specialty"
                checked={drugForm.is_specialty}
                onCheckedChange={(checked) => setDrugForm({ ...drugForm, is_specialty: checked })}
              />
              <Label htmlFor="is_specialty">Specialty</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="requires_prior_auth"
                checked={drugForm.requires_prior_auth}
                onCheckedChange={(checked) => setDrugForm({ ...drugForm, requires_prior_auth: checked })}
              />
              <Label htmlFor="requires_prior_auth">Prior Auth</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="requires_step_therapy"
                checked={drugForm.requires_step_therapy}
                onCheckedChange={(checked) => setDrugForm({ ...drugForm, requires_step_therapy: checked })}
              />
              <Label htmlFor="requires_step_therapy">Step Therapy</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDrugDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveDrug}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Network Dialog */}
      <Dialog open={showNetworkDialog} onOpenChange={setShowNetworkDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingNetwork ? 'Edit Pharmacy Network' : 'Add Pharmacy Network'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="md:col-span-1">
              <Label htmlFor="network_name">Network Name *</Label>
              <Input
                id="network_name"
                value={networkForm.network_name}
                onChange={(e) => setNetworkForm({ ...networkForm, network_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="network_type">Network Type</Label>
              <Select
                value={networkForm.network_type}
                onValueChange={(value) => setNetworkForm({ ...networkForm, network_type: value as PharmacyNetwork['network_type'] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="mail_order">Mail Order</SelectItem>
                  <SelectItem value="specialty">Specialty</SelectItem>
                  <SelectItem value="preferred">Preferred</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="geographic_coverage">Geographic Coverage</Label>
              <Input
                id="geographic_coverage"
                value={networkForm.geographic_coverage}
                onChange={(e) => setNetworkForm({ ...networkForm, geographic_coverage: e.target.value })}
                placeholder="e.g., Nationwide, Regional, State"
              />
            </div>
            <div>
              <Label htmlFor="pharmacy_count">Pharmacy Count</Label>
              <Input
                id="pharmacy_count"
                type="number"
                value={networkForm.pharmacy_count}
                onChange={(e) => setNetworkForm({ ...networkForm, pharmacy_count: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label htmlFor="discount_percentage">Discount Percentage</Label>
              <div className="relative">
                <Input
                  id="discount_percentage"
                  type="number"
                  value={networkForm.discount_percentage}
                  onChange={(e) => setNetworkForm({ ...networkForm, discount_percentage: parseFloat(e.target.value) || 0 })}
                  className="pr-7"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">%</span>
              </div>
            </div>
            <div>
              <Label htmlFor="dispensing_fee">Dispensing Fee</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="dispensing_fee"
                  type="number"
                  value={networkForm.dispensing_fee}
                  onChange={(e) => setNetworkForm({ ...networkForm, dispensing_fee: parseFloat(e.target.value) || 0 })}
                  className="pl-7"
                />
              </div>
            </div>
            <div className="md:col-span-2 flex items-center space-x-2">
              <Switch
                id="is_preferred"
                checked={networkForm.is_preferred}
                onCheckedChange={(checked) => setNetworkForm({ ...networkForm, is_preferred: checked })}
              />
              <Label htmlFor="is_preferred">Preferred Network</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNetworkDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveNetwork}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prior Authorization Dialog */}
      <Dialog open={showPriorAuthDialog} onOpenChange={setShowPriorAuthDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPriorAuth ? 'Edit Prior Authorization Rule' : 'Add Prior Authorization Rule'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div>
              <Label htmlFor="pa_drug_name">Drug Name *</Label>
              <Input
                id="pa_drug_name"
                value={priorAuthForm.drug_name}
                onChange={(e) => setPriorAuthForm({ ...priorAuthForm, drug_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="pa_drug_class">Drug Class</Label>
              <Input
                id="pa_drug_class"
                value={priorAuthForm.drug_class}
                onChange={(e) => setPriorAuthForm({ ...priorAuthForm, drug_class: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="approval_duration_days">Approval Duration (days)</Label>
              <Input
                id="approval_duration_days"
                type="number"
                value={priorAuthForm.approval_duration_days}
                onChange={(e) => setPriorAuthForm({ ...priorAuthForm, approval_duration_days: parseInt(e.target.value) || 365 })}
              />
            </div>
            <div>
              <Label htmlFor="auto_approve_criteria">Auto-Approve Criteria (optional)</Label>
              <Input
                id="auto_approve_criteria"
                value={priorAuthForm.auto_approve_criteria || ''}
                onChange={(e) => setPriorAuthForm({ ...priorAuthForm, auto_approve_criteria: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <h4 className="text-sm font-medium mb-2">Approval Criteria</h4>
              <hr className="border-border mb-2" />
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Add Criteria"
                  value={criteriaInput}
                  onChange={(e) => setCriteriaInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCriteria())}
                />
                <Button type="button" variant="outline" onClick={addCriteria}>Add</Button>
              </div>
              <div className="flex gap-2 flex-wrap">
                {priorAuthForm.criteria?.map((criterion, idx) => (
                  <Badge key={idx} variant="secondary" className="gap-1">
                    {criterion}
                    <button onClick={() => removeCriteria(idx)}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <h4 className="text-sm font-medium mb-2">Required Documentation</h4>
              <hr className="border-border mb-2" />
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Add Required Document"
                  value={documentationInput}
                  onChange={(e) => setDocumentationInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDocumentation())}
                />
                <Button type="button" variant="outline" onClick={addDocumentation}>Add</Button>
              </div>
              <div className="flex gap-2 flex-wrap">
                {priorAuthForm.required_documentation?.map((doc, idx) => (
                  <Badge key={idx} variant="secondary" className="gap-1">
                    {doc}
                    <button onClick={() => removeDocumentation(idx)}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPriorAuthDialog(false)}>Cancel</Button>
            <Button onClick={handleSavePriorAuth}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step Therapy Dialog */}
      <Dialog open={showStepTherapyDialog} onOpenChange={setShowStepTherapyDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingStepTherapy ? 'Edit Step Therapy Protocol' : 'Add Step Therapy Protocol'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="target_drug">Target Drug *</Label>
                <Input
                  id="target_drug"
                  value={stepTherapyForm.target_drug}
                  onChange={(e) => setStepTherapyForm({ ...stepTherapyForm, target_drug: e.target.value })}
                  required
                  placeholder="The drug that requires step therapy before approval"
                />
              </div>
              <div>
                <Label htmlFor="required_duration_days">Required Duration (days)</Label>
                <Input
                  id="required_duration_days"
                  type="number"
                  value={stepTherapyForm.required_duration_days}
                  onChange={(e) => setStepTherapyForm({ ...stepTherapyForm, required_duration_days: parseInt(e.target.value) || 30 })}
                />
                <p className="text-xs text-muted-foreground mt-1">Days on first-line drug</p>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium mb-2">Required First-Line Agents</h4>
              <hr className="border-border mb-2" />
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Add First-Line Drug"
                  value={firstLineInput}
                  onChange={(e) => setFirstLineInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFirstLine())}
                />
                <Button type="button" variant="outline" onClick={addFirstLine}>Add</Button>
              </div>
              <div className="flex gap-2 flex-wrap">
                {stepTherapyForm.required_first_line?.map((drug, idx) => (
                  <Badge key={idx} variant="default" className="gap-1">
                    {drug}
                    <button onClick={() => removeFirstLine(idx)}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium mb-2">Exception Criteria</h4>
              <hr className="border-border mb-2" />
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Add Exception"
                  value={exceptionInput}
                  onChange={(e) => setExceptionInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addException())}
                />
                <Button type="button" variant="outline" onClick={addException}>Add</Button>
              </div>
              <div className="flex gap-2 flex-wrap">
                {stepTherapyForm.exception_criteria?.map((exc, idx) => (
                  <Badge key={idx} variant="secondary" className="gap-1">
                    {exc}
                    <button onClick={() => removeException(idx)}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStepTherapyDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveStepTherapy}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
